import { useEffect, useState, useMemo } from 'react'
import toast from 'react-hot-toast'
import { sellersApi, collaboratorsApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'

const PERU_DEPARTMENTS: Record<string, Record<string, string[]>> = {
  'Lima': {
    'Lima': ['Cercado de Lima', 'Ancón', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo', 'Chorrillos', 'Cieneguilla', 'Comas', 'El Agustino', 'Independencia', 'Jesús María', 'Jesús Nazareno', 'La Molina', 'La Victoria', 'Lince', 'Los Olivos', 'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Magdalena Vieja', 'Maringá', 'Matta Sur', 'Pachacámac', 'Pachacutec', 'Punta Hermosa', 'Punta Negra', 'Rímac', 'San Bartolo', 'San Borja', 'San Isidro', 'San Martín de Porres', 'San Juan de Miraflores', 'San Juan de Lurigancho', 'San Luis', 'San Miguel', 'Santa Anita', 'Santa María del Mar', 'Santa Rosa', 'Santiago de Surco', 'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo'],
  },
  'Arequipa': {
    'Arequipa': ['Arequipa', 'Alto Selva Alegre', 'Cayma', 'Cerro Colorado', 'Characato', 'Chiguata', 'Jacobo Hunter', 'La Joya', 'Mariano Melgar', 'Miraflores', 'Mollebaya', 'Paucarpata', 'Pocsi', 'Polobaya', 'Quequeña', 'Sabandía', 'Sachaca', 'San Juan de Siguas', 'San Juan de Tarucani', 'Santa Isabel de Siguas', 'Santa Rita de Siguas', 'Socabaya', 'Tiabaya', 'Uchumayo', 'Vítor', 'Yanahuara', 'Yarabamba', 'Yura'],
  },
  'Cusco': {
    'Cusco': ['Cusco', 'Acomayo', 'Calca', 'Chincheros', 'Coropuna', 'Espinar', 'La Convención', 'Paruro', 'Paucartambo', 'Quispicanchi', 'Urubamba'],
  },
  'Junín': {
    'Huancayo': ['Huancayo', 'Chupaca', 'Concepción', 'Jauja', 'Junín', 'Satipo', 'Tarma'],
  },
  'Piura': {
    'Piura': ['Piura', 'Ayabaca', 'Huancabamba', 'Morropón', 'Paita', 'Sechura', 'Sullana', 'Talara'],
  },
  'La Libertad': {
    'Trujillo': ['Trujillo', 'Ascope', 'Chepen', 'Julcán', 'Otuzco', 'Pacasmayo', 'Pataz', 'Sánchez Carrión', 'Santiago de Chuco', 'Virú'],
  },
  'Lambayeque': {
    'Chiclayo': ['Chiclayo', 'Ferreñafe', 'Lambayeque'],
  },
  'Callao': {
    'Callao': ['Callao', 'Bellavista', 'Carmen de la Legua', 'La Perla', 'La Punta', 'Ventanilla'],
  },
}

const COLLABORATOR_STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
}

const PANEL_OPTIONS: { key: string; label: string }[] = [
  { key: 'vendedor', label: 'Vendedor' },
  { key: 'logistica', label: 'Logística' },
  { key: 'rrhh', label: 'RRHH' },
  { key: 'finanzas', label: 'Finanzas' },
]

interface Collaborator {
  id: number
  email: string
  full_name: string
  role: string
  status: string
  dni?: string
  phone?: string
  residency?: string
  created_at: string
  permissions?: { edit_customers?: boolean; edit_sales?: boolean }
  panels: string[]
}

export default function CollaboratorsPage() {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    dni: '',
    phone: '',
  })
  const [formPanels, setFormPanels] = useState<string[]>([])
  const [dept, setDept] = useState('')
  const [prov, setProv] = useState('')
  const [dist, setDist] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [togglingPanelKey, setTogglingPanelKey] = useState<string | null>(null)

  useEffect(() => {
    fetchCollaborators()
  }, [])

  const provinces = useMemo(() => {
    if (!dept || !PERU_DEPARTMENTS[dept]) return []
    return Object.keys(PERU_DEPARTMENTS[dept])
  }, [dept])

  const districts = useMemo(() => {
    if (!dept || !prov) return []
    return PERU_DEPARTMENTS[dept]?.[prov] || []
  }, [dept, prov])

  const fetchCollaborators = async () => {
    try {
      setLoadError('')
      const response = await collaboratorsApi.list()
      setCollaborators(response.data.collaborators || [])
    } catch (error) {
      console.error('Error fetching collaborators:', error)
      setLoadError('No se pudieron cargar los colaboradores. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({ email: '', full_name: '', dni: '', phone: '' })
    setFormPanels([])
    setDept('')
    setProv('')
    setDist('')
    setCreateError('')
  }

  const toggleFormPanel = (panel: string) => {
    setFormPanels((prev) =>
      prev.includes(panel) ? prev.filter((p) => p !== panel) : [...prev, panel]
    )
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formPanels.length === 0) {
      setCreateError('Selecciona al menos un panel de acceso')
      return
    }
    const residencyParts = ['Perú', dept, prov, dist].filter(Boolean)
    setCreating(true)
    setCreateError('')
    try {
      await collaboratorsApi.create({
        email: formData.email,
        full_name: formData.full_name,
        dni: formData.dni || undefined,
        phone: formData.phone || undefined,
        residency: residencyParts.length > 1 ? residencyParts.join(', ') : undefined,
        panels: formPanels,
      })
      toast.success('Colaborador creado')
      setShowAddModal(false)
      resetForm()
      fetchCollaborators()
    } catch (error: any) {
      setCreateError(error.response?.data?.error || 'Error al crear el colaborador')
    } finally {
      setCreating(false)
    }
  }

  const handleToggleStatus = async (id: number, currentStatus: string) => {
    try {
      await sellersApi.updateStatus(id, {
        status: currentStatus === 'active' ? 'inactive' : 'active',
      })
      fetchCollaborators()
    } catch (error) {
      console.error('Error updating collaborator status:', error)
      toast.error('Error al actualizar el estado del colaborador')
    }
  }

  const handleTogglePermission = async (id: number, key: 'edit_customers' | 'edit_sales', currentValue: boolean) => {
    try {
      await sellersApi.updatePermissions(id, { [key]: !currentValue })
      fetchCollaborators()
    } catch (error) {
      console.error('Error updating collaborator permissions:', error)
      toast.error('Error al actualizar los permisos')
    }
  }

  const handleTogglePanel = async (collaborator: Collaborator, panel: string) => {
    const nextPanels = collaborator.panels.includes(panel)
      ? collaborator.panels.filter((p) => p !== panel)
      : [...collaborator.panels, panel]
    const rowKey = `${collaborator.id}-${panel}`
    setTogglingPanelKey(rowKey)
    try {
      await collaboratorsApi.updatePanels(collaborator.id, nextPanels)
      setCollaborators((prev) =>
        prev.map((c) => (c.id === collaborator.id ? { ...c, panels: nextPanels } : c))
      )
      toast.success('Paneles actualizados')
    } catch (error: any) {
      console.error('Error updating collaborator panels:', error)
      toast.error(error.response?.data?.error || 'Error al actualizar los paneles')
    } finally {
      setTogglingPanelKey(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Gestión de Colaboradores</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-terracotta-600 hover:bg-terracotta-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          Agregar Colaborador
        </button>
      </div>

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-espresso-100">
            <thead className="bg-cream-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Correo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">DNI</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Teléfono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Domicilio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Paneles</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Permisos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Creado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-cream-50 divide-y divide-espresso-100">
              {collaborators.map((collaborator) => {
                const hasVendedorPanel = collaborator.panels.includes('vendedor')
                return (
                  <tr key={collaborator.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{collaborator.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{collaborator.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{collaborator.dni || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{collaborator.phone || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">{collaborator.residency || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        collaborator.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {COLLABORATOR_STATUS_LABELS[collaborator.status] || collaborator.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-espresso-600 space-y-1">
                      {PANEL_OPTIONS.map((panel) => {
                        const rowKey = `${collaborator.id}-${panel.key}`
                        return (
                          <label key={panel.key} className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={collaborator.panels.includes(panel.key)}
                              disabled={togglingPanelKey === rowKey}
                              onChange={() => handleTogglePanel(collaborator, panel.key)}
                              className="h-3.5 w-3.5 text-terracotta-600 rounded border-espresso-300 focus:ring-terracotta-500 disabled:opacity-50"
                            />
                            {panel.label}
                          </label>
                        )
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-espresso-600 space-y-1">
                      {hasVendedorPanel ? (
                        <>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={collaborator.permissions?.edit_customers !== false}
                              onChange={() => handleTogglePermission(
                                collaborator.id, 'edit_customers', collaborator.permissions?.edit_customers !== false
                              )}
                              className="h-3.5 w-3.5 text-terracotta-600 rounded border-espresso-300 focus:ring-terracotta-500"
                            />
                            Editar clientes
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={collaborator.permissions?.edit_sales !== false}
                              onChange={() => handleTogglePermission(
                                collaborator.id, 'edit_sales', collaborator.permissions?.edit_sales !== false
                              )}
                              className="h-3.5 w-3.5 text-terracotta-600 rounded border-espresso-300 focus:ring-terracotta-500"
                            />
                            Editar ventas
                          </label>
                        </>
                      ) : (
                        <span className="text-espresso-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">
                      {new Date(collaborator.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <TableActionButton
                        onClick={() => handleToggleStatus(collaborator.id, collaborator.status)}
                        variant={collaborator.status === 'active' ? 'danger' : 'success'}
                      >
                        {collaborator.status === 'active' ? 'Desactivar' : 'Activar'}
                      </TableActionButton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
          {collaborators.length === 0 && (
            <div className="text-center py-8 text-espresso-500">No hay colaboradores registrados aún.</div>
          )}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <h3 className="text-lg font-medium text-espresso-800 mb-4">Agregar Colaborador</h3>
            <form onSubmit={handleCreate}>
              {createError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm mb-4" role="alert">
                  {createError}
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="collab-full-name" className="block text-sm font-medium text-espresso-700">Nombre Completo</label>
                <input id="collab-full-name" type="text" required value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1 block w-full border border-espresso-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-terracotta-500 focus:border-terracotta-500"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="collab-email" className="block text-sm font-medium text-espresso-700">Correo</label>
                <input id="collab-email" type="email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full border border-espresso-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-terracotta-500 focus:border-terracotta-500"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="collab-dni" className="block text-sm font-medium text-espresso-700">DNI / Pasaporte</label>
                <input id="collab-dni" type="text" value={formData.dni} placeholder="12345678"
                  onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                  className="mt-1 block w-full border border-espresso-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-terracotta-500 focus:border-terracotta-500"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="collab-phone" className="block text-sm font-medium text-espresso-700">Teléfono</label>
                <input id="collab-phone" type="tel" value={formData.phone} placeholder="999888777"
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full border border-espresso-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-terracotta-500 focus:border-terracotta-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-espresso-700 mb-1">Domicilio</label>
                <div className="bg-cream-50 rounded-md px-3 py-2 text-xs text-espresso-500 mb-2">Perú</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <select value={dept} onChange={(e) => { setDept(e.target.value); setProv(''); setDist('') }}
                    className="px-2 py-2 border border-espresso-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-terracotta-500">
                    <option value="">Depto.</option>
                    {Object.keys(PERU_DEPARTMENTS).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={prov} onChange={(e) => { setProv(e.target.value); setDist('') }} disabled={!dept}
                    className="px-2 py-2 border border-espresso-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-terracotta-500 disabled:bg-cream-200">
                    <option value="">Prov.</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <select value={dist} onChange={(e) => setDist(e.target.value)} disabled={!prov}
                    className="px-2 py-2 border border-espresso-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-terracotta-500 disabled:bg-cream-200">
                    <option value="">Distrito</option>
                    {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <fieldset className="mb-4">
                <legend className="block text-sm font-medium text-espresso-700 mb-1">Paneles de acceso</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PANEL_OPTIONS.map((panel) => (
                    <label key={panel.key} className="flex items-center gap-1.5 text-sm text-espresso-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formPanels.includes(panel.key)}
                        onChange={() => toggleFormPanel(panel.key)}
                        className="h-4 w-4 text-terracotta-600 rounded border-espresso-300 focus:ring-terracotta-500"
                      />
                      {panel.label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <p className="text-xs text-espresso-500 mb-4">
                La cuenta se crea sin contraseña — la persona la establece luego desde "¿Olvidaste tu contraseña?" en la pantalla de inicio de sesión.
              </p>
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => { setShowAddModal(false); resetForm() }}
                  className="px-4 py-2 border border-espresso-300 text-sm font-medium rounded-md text-espresso-700 bg-cream-50 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={creating}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-terracotta-600 hover:bg-terracotta-700 disabled:opacity-50">
                  {creating ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}
    </div>
  )
}
